import { Box } from '@mui/material';
import { ORANGE, WHITE } from 'constants/colors';
import { isValidElement, type ReactNode } from 'react';
import styled from 'styled-components';
import { StyledExtraSmallIcon } from 'components/shared/Icon/Icon.styles';
import NonRecurringIconBlack from '../NonRecurringIconBlack';

type SignalRowProps = {
  hasNote?: boolean;
  appointmentType?: string;
  hasOverlap?: boolean;
  isNotInSync?: boolean;
  hasNoSyncStatus?: boolean;
  isPartiallyImported?: boolean;
};

const IconContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
`;

const OverflowBadge = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  height: '14px',
  width: '14px',
  paddingLeft: '2px',
  paddingBottom: '1px',
  borderRadius: '50%',
  boxSizing: 'border-box',
  backgroundColor: ORANGE,
  color: WHITE,
  fontSize: '9px',
  cursor: 'pointer',
  position: 'relative'
});

const ActivitySignalRow = ({ children }: { children: ReactNode[] }) => {
  const elements: ReactNode[] = children.filter((child: unknown) =>
    isValidElement(child)
  ) as ReactNode[];

  const iconCount = elements.length;

  return (
    <Box
      data-signal-count={iconCount}
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      height="auto"
      className="signal-icon-column"
    >
      {elements}
      {iconCount > 1 && (
        <>
          <OverflowBadge className="overflow-badge badge--all">
            +{iconCount}
          </OverflowBadge>
          {
            /* Dynamically render overflow badges (+2, +3, +4, etc.) for each icon beyond the first.
                CSS container queries in CalendarAppointment.styles.tsx control which badges are visible
                based on appointment height and icon count. All badges are rendered with display: none
                by default, and CSS selectively applies display: flex to show the appropriate badge. */
            [...Array(iconCount - 1)].map((_, index) => {
              const countLabel = index + 2;
              return (
                <OverflowBadge
                  key={index}
                  className={`overflow-badge badge--plus${countLabel}`}
                >
                  +{countLabel}
                </OverflowBadge>
              );
            })
          }
        </>
      )}
    </Box>
  );
};

const StyledCalendarAppointmentSignalRow = styled(Box)({
  display: 'flex',
  position: 'absolute',
  top: '3px',
  right: '3px',
  alignItems: 'center',
  flexWrap: 'wrap',
  justifyContent: 'center',
  maxWidth: '20px',
  boxSizing: 'border-box'
});

const CalendarAppointmentSignalRow = ({
  hasNote,
  appointmentType,
  hasOverlap,
  isNotInSync,
  hasNoSyncStatus,
  isPartiallyImported
}: SignalRowProps) => {
  return (
    <StyledCalendarAppointmentSignalRow>
      <ActivitySignalRow>
        {appointmentType === 'SERIES_INSTANCE' && (
          <IconContainer className="signal-icon icon-recurring">
            <StyledExtraSmallIcon type="arrows-repeat" />
          </IconContainer>
        )}
        {appointmentType === 'SERIES_EXCEPTION' && (
          <IconContainer className="signal-icon icon-exception">
            <NonRecurringIconBlack />
          </IconContainer>
        )}
        {hasOverlap && (
          <IconContainer className="signal-icon icon-overlap">
            <StyledExtraSmallIcon type="calendar-xmark" />
          </IconContainer>
        )}
        {isNotInSync && (
          <IconContainer className="signal-icon icon-sync">
            <StyledExtraSmallIcon type="cloud-exclamation" />
          </IconContainer>
        )}
        {hasNoSyncStatus && (
          <IconContainer className="signal-icon icon-sync">
            <StyledExtraSmallIcon type="cloud-question" />
          </IconContainer>
        )}
        {isPartiallyImported && (
          <IconContainer className="signal-icon icon-partially-imported">
            <StyledExtraSmallIcon type="cloud-question" />
          </IconContainer>
        )}
        {hasNote && (
          <IconContainer className="signal-icon icon-note">
            <StyledExtraSmallIcon type="note" />
          </IconContainer>
        )}
      </ActivitySignalRow>
    </StyledCalendarAppointmentSignalRow>
  );
};

export default CalendarAppointmentSignalRow;
